import { useState, useEffect } from "react";
import { cruises, expeditions } from "@/data/cruises";
import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { QuoteRequestDialog } from "@/components/common/QuoteRequestDialog";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Ship, Anchor, ArrowRight, Star, Check, Compass, Mountain } from "lucide-react";

const REGION_COLORS = {
  Antarctica: "bg-sky-100 text-sky-700",
  Arctic: "bg-emerald-100 text-emerald-700",
  Exclusive: "bg-amber-100 text-amber-700",
};

export default function CruisesPage() {
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedCruise, setSelectedCruise] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleQuote = (item, isExpedition = false) => {
    setSelectedCruise({
      type: "cruise",
      cruiseName: item.title,
      ship: item.ship,
      duration: item.duration,
    });
    setQuoteDialogOpen(true);
  };

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <SeoHead
        title="Cruises & Polar Expeditions"
        description="Book cruise packages and polar expeditions with Tripsyfy. Caribbean, Mediterranean, Antarctica, and Arctic voyages expertly arranged."
        canonical="https://tripsyfy.com/cruises"
      />
      <Navbar />

      {/* Page header */}
      <section className="relative pt-28 pb-14 bg-primary" data-testid="cruises-header">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">Set Sail</p>
          <h1 className="heading-xl text-white mb-3">Cruises & Polar Expeditions</h1>
          <p className="text-base text-white/70 max-w-lg leading-relaxed">
            From tropical islands to the ends of the Earth — classic cruise packages and exclusive small-ship polar expeditions, all expertly arranged for you.
          </p>
        </div>
      </section>

      {/* ── Classic Cruises ── */}
      <section className="section-padding bg-surface-subtle" data-testid="cruises-list">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <p className="overline mb-2">Classic Voyages</p>
            <h2 className="heading-md text-primary">Cruise Packages</h2>
          </div>
          <div className="space-y-6">
            {cruises.map((cruise, i) => (
              <AnimatedSection key={cruise.id} delay={Math.min(i * 0.08, 0.3)}>
                <div className="group bg-white rounded-xl shadow-card hover:shadow-card-hover transition-shadow duration-200 overflow-hidden">
                  <div className="flex flex-col lg:flex-row">
                    <div className="relative overflow-hidden lg:w-2/5 aspect-[16/10] lg:aspect-auto">
                      <img
                        src={cruise.image}
                        alt={cruise.title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.2,0,0,1)] group-hover:scale-105"
                        loading="lazy"
                      />
                      <Badge variant="accent" className="absolute top-4 left-4">{cruise.duration}</Badge>
                    </div>
                    <div className="p-6 lg:w-3/5 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <Ship className="h-4 w-4 text-accent shrink-0" />
                        <span className="text-xs text-muted-foreground font-medium">{cruise.ship}</span>
                      </div>
                      <h3 className="heading-sm text-primary mb-2">{cruise.title}</h3>
                      <div className="flex items-center gap-1.5 mb-3">
                        <Star className="h-3.5 w-3.5 fill-accent text-accent" />
                        <span className="text-sm font-semibold text-primary">{cruise.rating}</span>
                        <span className="text-xs text-muted-foreground">({cruise.reviews} reviews)</span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">{cruise.description}</p>
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-primary mb-2">Ports of Call</p>
                        <div className="flex flex-wrap gap-1.5">
                          {cruise.ports.map((port) => (
                            <span key={port} className="text-xs bg-surface-subtle text-muted-foreground px-2.5 py-1 rounded-pill flex items-center gap-1">
                              <Anchor className="h-3 w-3 shrink-0" /> {port}
                            </span>
                          ))}
                        </div>
                      </div>
                      {cruise.highlights && (
                        <div className="mb-5">
                          <p className="text-xs font-semibold text-primary mb-2">Highlights</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {cruise.highlights.slice(0, 4).map((h, j) => (
                              <span key={j} className="text-xs text-muted-foreground flex items-center gap-1.5">
                                <Check className="h-3 w-3 text-accent shrink-0" /> {h}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-auto pt-4 border-t border-border/60">
                        <div>
                          <p className="text-xs text-muted-foreground">Starting from</p>
                          <p className="text-2xl font-bold text-primary">${cruise.startingPrice.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">per person</p>
                        </div>
                        <Button onClick={() => handleQuote(cruise)} className="bg-accent text-white hover:bg-accent/90 gap-2">
                          Request Quote <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Polar Expeditions ── */}
      <section className="section-padding bg-primary" data-testid="expeditions-list">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="mb-8">
              <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-2">Exclusive Access</p>
              <h2 className="heading-md text-white mb-3">Polar Expeditions</h2>
              <p className="text-white/60 text-sm leading-relaxed max-w-xl">
                Small-ship expeditions to Antarctica and the Arctic — Zodiac landings, expert naturalists, and wildlife encounters found nowhere else on Earth.
              </p>
            </div>
          </AnimatedSection>
          <div className="space-y-6">
            {expeditions.map((exp, i) => (
              <AnimatedSection key={exp.id} delay={Math.min(i * 0.08, 0.3)}>
                <div className="group bg-white rounded-xl shadow-card-lift hover:shadow-card-hover transition-shadow duration-200 overflow-hidden">
                  <div className="flex flex-col lg:flex-row">
                    <div className="relative overflow-hidden lg:w-2/5 aspect-[16/10] lg:aspect-auto">
                      <img
                        src={exp.image}
                        alt={exp.title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.2,0,0,1)] group-hover:scale-105"
                        loading="lazy"
                      />
                      <Badge variant="accent" className="absolute top-4 left-4">{exp.duration}</Badge>
                      <span className={`absolute top-4 right-4 text-xs font-semibold px-2.5 py-1 rounded-full ${REGION_COLORS[exp.badge] || "bg-gray-100 text-gray-600"}`}>
                        {exp.badge}
                      </span>
                    </div>
                    <div className="p-6 lg:w-3/5 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <Compass className="h-4 w-4 text-accent shrink-0" />
                        <span className="text-xs text-muted-foreground font-medium">{exp.ship}</span>
                      </div>
                      <h3 className="heading-sm text-primary mb-2">{exp.title}</h3>
                      <div className="flex items-center gap-1.5 mb-3">
                        <Star className="h-3.5 w-3.5 fill-accent text-accent" />
                        <span className="text-sm font-semibold text-primary">{exp.rating}</span>
                        <span className="text-xs text-muted-foreground">({exp.reviews} reviews)</span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">{exp.description}</p>
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-primary mb-2">Destinations</p>
                        <div className="flex flex-wrap gap-1.5">
                          {exp.ports.map((port) => (
                            <span key={port} className="text-xs bg-surface-subtle text-muted-foreground px-2.5 py-1 rounded-pill flex items-center gap-1">
                              <Mountain className="h-3 w-3 shrink-0" /> {port}
                            </span>
                          ))}
                        </div>
                      </div>
                      {exp.highlights && (
                        <div className="mb-5">
                          <p className="text-xs font-semibold text-primary mb-2">Highlights</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {exp.highlights.slice(0, 4).map((h, j) => (
                              <span key={j} className="text-xs text-muted-foreground flex items-center gap-1.5">
                                <Check className="h-3 w-3 text-accent shrink-0" /> {h}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-auto pt-4 border-t border-border/60">
                        <div>
                          <p className="text-xs text-muted-foreground">Starting from</p>
                          <p className="text-2xl font-bold text-primary">${exp.startingPrice.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">per person</p>
                        </div>
                        <Button onClick={() => handleQuote(exp)} className="bg-accent text-white hover:bg-accent/90 gap-2">
                          Request Quote <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <QuoteRequestDialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen} quoteData={selectedCruise} />
    </div>
  );
}
